import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { formatMoney, moneyFromCents } from '@wodi/domain';
import { visitShaAmountsSchema } from '@wodi/shared';
import { Button, LoadingBlock, Notice } from '@wodi/ui';

import { prepareClaim } from '../api/claims.ts';
import { api } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import billing from '../billing/billing.module.css';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../stock/common.tsx';
import styles from './sha.module.css';

// The SHA part of the bill (docs/plans/phase1-sha.md §9.7; checklist 22): what SHA pays and
// what the patient pays, by intervention, from the charges' own snapshots; and the claim.

const kes = (cents: string) => formatMoney(moneyFromCents(BigInt(cents)));

export function BillShaSummary({ visitId }: { visitId: string }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const amounts = useQuery({
    queryKey: ['visits', visitId, 'sha-amounts'],
    queryFn: () => api('GET', `/visits/${visitId}/sha/amounts`, { schema: visitShaAmountsSchema }),
  });
  const prepare = useMutation({
    mutationFn: () => prepareClaim(visitId),
    onSuccess: async ({ claimId }) => {
      await queryClient.invalidateQueries({ queryKey: ['visits', visitId, 'sha-amounts'] });
      await navigate({ to: '/claims/$claimId', params: { claimId } });
    },
  });
  return (
    <section className={billing.section} aria-labelledby="bill-sha">
      <h2 id="bill-sha" className={billing.sectionHeading}>
        SHA
      </h2>
      {amounts.isPending ? <LoadingBlock label="Loading SHA amounts" rows={2} /> : null}
      {amounts.isError ? (
        <Notice tone="error">
          {errorText(amounts.error, "SHA amounts couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {amounts.isSuccess ? (
        <>
          <p className={styles.schemeLine}>
            {`SHA pays ${kes(amounts.data.totals.shaCents)}. Patient pays ${kes(amounts.data.totals.patientCents)}.`}
          </p>
          {amounts.data.interventions.length > 0 ? (
            <table className={styles.table} aria-label="SHA amounts by intervention">
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Intervention</th>
                  <th scope="col" className={styles.number}>
                    Quantity
                  </th>
                  <th scope="col" className={styles.number}>
                    SHA pays
                  </th>
                  <th scope="col" className={styles.number}>
                    Patient pays
                  </th>
                </tr>
              </thead>
              <tbody>
                {amounts.data.interventions.map((i) => (
                  <tr key={i.code}>
                    <td className={styles.id}>{i.code}</td>
                    <td>{i.display}</td>
                    <td className={styles.number}>{i.quantity}</td>
                    <td className={styles.number}>{kes(i.shaCents)}</td>
                    <td className={styles.number}>{kes(i.patientCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {amounts.data.excluded.length > 0 ? (
            <ul className={styles.reasons} aria-label="Not on the SHA claim">
              {amounts.data.excluded.map((e) => (
                <li key={e.chargeId}>{e.words}</li>
              ))}
            </ul>
          ) : null}
          {amounts.data.claim === null ? (
            session !== null && can(session, 'claims.submit') ? (
              <div className={styles.actions}>
                <Button
                  isDisabled={!online}
                  isPending={prepare.isPending}
                  pendingLabel="Preparing…"
                  onPress={() => {
                    prepare.mutate();
                  }}
                >
                  Prepare claim
                </Button>
              </div>
            ) : (
              <p className={styles.detail}>No SHA claim yet.</p>
            )
          ) : (
            <p>
              {'Claim '}
              {session !== null && can(session, 'claims.read') ? (
                <Link
                  to="/claims/$claimId"
                  params={{ claimId: amounts.data.claim.id }}
                  className={styles.id}
                >
                  {amounts.data.claim.claimNumber}
                </Link>
              ) : (
                <span className={styles.id}>{amounts.data.claim.claimNumber}</span>
              )}
              {`: ${amounts.data.claim.sentence}`}
            </p>
          )}
          {prepare.isError ? (
            <Notice tone="error">
              {errorText(prepare.error, "The claim couldn't be prepared. Try again.")}
            </Notice>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
